import {
    Box,
    Button,
    ButtonBase,
    Paper,
    Stack,
    useTheme,
    useMediaQuery, Dialog, DialogTitle, IconButton, Divider, DialogContent, InputAdornment, TextField,
} from "@mui/material";
import React, {useEffect, useMemo, useRef, useState} from "react";
import { CervinoHeaderText } from "../components/typography/CervinoHeaderText";
import SMFooter from "../components/marketing/SMFooter";
import CervinoText from "../components/typography/CervinoText";
import { PromoCtaCard } from "../components/marketing/PromoCtaCard";
import LocalShippingIcon from "@mui/icons-material/LocalShippingTwoTone";
import StoreIcon from "@mui/icons-material/StoreTwoTone";
import FoodBankTwoToneIcon from "@mui/icons-material/FoodBankTwoTone";
import MapBoxMap from "../components/maps/MapBoxMap";
import { STORES } from "../data/mapBoxStores";
import PlaceIcon from '@mui/icons-material/Place';
import SearchIcon from '@mui/icons-material/Search';

// miles-based nearest store helpers
import { getNearestStores, sortStoresByDistance } from "../utils/geo";
import iconUrl from "../assets/icons/SM_star.png";
import IconDividerLine from "../components/marketing/IconDividerLine";
import MarketingPhrasesRowV2 from "../components/marketing/MarketingPhrasesRowV2";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import SMButtonBrown from "../components/navigation/SMButtonBrown";
import ShimmerOverlay from "../components/animations/ShimmerOverlay";

export default function WhereToBuyPage() {
    const SALES_EMAIL = "jp.madrazo@sanmiguel.me";
    const WHATSAPP_NUMBER = "16193842096";

    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("md")); // xs + sm
    const mapStores = useMemo(() => STORES, []);

    const [contactSalesOpen, setContactSalesOpen] = useState(false);
    const openContactSalesDialog = () => setContactSalesOpen(true);
    const closeContactSalesDialog = () => setContactSalesOpen(false);

    // user coordinates [lng, lat]
    const [userCoords, setUserCoords] = useState(null);
    const [geoError, setGeoError] = useState("");
    const DEFAULT_CENTER = [-98.5795, 39.8283];

    // selected store from list -> drives map flyTo + popup
    const [selectedStoreId, setSelectedStoreId] = useState(null);
    const [storeQuery, setStoreQuery] = useState("");

    const [geoResolved, setGeoResolved] = useState(false);
    const [mapReady, setMapReady] = useState(false);

    const mapAnchorRef = useRef(null);
    const showMapShimmer = !(geoResolved && mapReady);
    const scrollToMap = () => {
        mapAnchorRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    useEffect(() => {
        if (!navigator.geolocation) {
            setGeoError("Geolocation is not supported in this browser.");
            setGeoResolved(true);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setUserCoords([pos.coords.longitude, pos.coords.latitude]);
                setGeoError("");
                setGeoResolved(true);
            },
            (err) => {
                setGeoError(err?.message || "Location access denied.");
                setGeoResolved(true);
            },
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 }
        );
    }, []);

    // stores + distance for list (TOP 3 on xs/sm, TOP 10 on md+)
    const nearestStoresWithDistance = useMemo(() => {
        if (!userCoords) return [];
        return sortStoresByDistance(userCoords, STORES, "miles").slice(0, 10);
    }, [userCoords]);

    const fitStores = useMemo(() => {
        if (!userCoords) return STORES;      // fallback
        return getNearestStores(userCoords, STORES, 10, "miles"); // focus area only
    }, [userCoords]);

    const filteredNearestStores = useMemo(() => {
        const q = storeQuery.trim().toLowerCase();
        if (!q) return nearestStoresWithDistance;

        return nearestStoresWithDistance.filter((s) => {
            const haystack = `${s.name ?? ""} ${s.address ?? ""} ${s.city ?? ""} ${s.state ?? ""} ${s.zip ?? ""}`.toLowerCase();
            return haystack.includes(q);
        });
    }, [storeQuery, nearestStoresWithDistance]);

    useEffect(() => {
        setMapReady(false);
    }, [userCoords]);

    const NearbyLocationsPanel = (
        <Paper
            elevation={0}
            sx={{
                width: { xs: "100%", md: 300 },
                p: 1.5,
                overflow: "auto",
                borderRadius: 4,
                backgroundColor: "rgba(255, 255, 255, 0.18)",
                backdropFilter: "blur(14px) saturate(160%)",
                WebkitBackdropFilter: "blur(14px) saturate(160%)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                scrollbarWidth: "thin",
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: 16,
                    pointerEvents: "none",
                    background:
                        "linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.12) 35%, rgba(255,255,255,0.06) 60%, rgba(255,255,255,0.0) 100%)",
                },
                "& > *": { position: "relative", zIndex: 1 },
            }}
        >
            <CervinoText variant="h5" sx={{ mb: 1, fontWeight: 900 }}>
                Nearby Locations
            </CervinoText>

            <TextField
                value={storeQuery}
                onChange={(e) => setStoreQuery(e.target.value)}
                placeholder="Search by store or address..."
                size="small"
                fullWidth
                sx={{
                    mt: 1,
                    "& .MuiOutlinedInput-root": {
                        borderRadius: 999,
                        backgroundColor: "rgba(255,255,255,0.18)",
                        backdropFilter: "blur(10px) saturate(160%)",
                        WebkitBackdropFilter: "blur(10px) saturate(160%)",
                    },
                }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    },
                }}
            />

            {!userCoords ? (
                <CervinoText variant="body2" color="text.secondary">
                    {geoError ? geoError : "Allow location access to see stores near you."}
                </CervinoText>
            ) : (
                <Box
                    sx={{
                        mt: 1,
                        // On xs/sm constrain height so ~3 items show, and enable scrolling.
                        maxHeight: { xs: 320, sm: 320, md: "none" },
                        overflowY: { xs: "auto", sm: "auto", md: "visible" },
                        pr: { xs: 0.5, sm: 0.5, md: 0 }, // little room for scrollbar
                        scrollbarWidth: "thin",
                    }}
                >
                    <Stack spacing={1.25} sx={{ mt: 1 }}>
                        {filteredNearestStores.map((s) => {
                            const isSelected = String(selectedStoreId) === String(s.id);

                            return (
                                <ButtonBase
                                    key={s.id}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => setSelectedStoreId(s.id)}
                                    focusRipple
                                    sx={{
                                        width: "100%",
                                        textAlign: "left",
                                        borderRadius: 3,
                                        display: "block",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: 1.25,
                                            borderRadius: 3,
                                            backgroundColor: isSelected
                                                ? theme.palette.background.default
                                                : "rgba(255,255,255,0.12)",
                                            backdropFilter: "blur(10px) saturate(160%)",
                                            WebkitBackdropFilter: "blur(10px) saturate(160%)",
                                            border: "1px solid",
                                            borderColor: isSelected
                                                ? theme.palette.primary.main
                                                : "rgba(255,255,255,0.28)",
                                            boxShadow: isSelected
                                                ? "0 10px 18px rgba(0,0,0,0.14)"
                                                : "0 8px 16px rgba(0,0,0,0.10)",
                                            cursor: "pointer",
                                            transition:
                                                "transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease",
                                            "&:hover": {
                                                transform: "translateY(-1px)",
                                                borderColor: theme.palette.primary.main,
                                                boxShadow: "0 14px 24px rgba(0,0,0,0.16)",
                                            },
                                        }}
                                    >
                                        <CervinoText sx={{ fontWeight: 900, color: theme.palette.primary.main }}>
                                            {s.name}
                                        </CervinoText>

                                        <CervinoText sx={{ fontWeight: 700, opacity: 0.9 }}>
                                            {s.address}
                                        </CervinoText>

                                        <Box sx={{ mt: 0.75, display: "flex", alignItems: "center" }}>
                                            <Button
                                                component="a"
                                                href={
                                                    s.addressUrl ||
                                                    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                        s.address
                                                    )}`
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                startIcon={<PlaceIcon />}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    borderRadius: 999,
                                                    fontWeight: 900,
                                                    textTransform: "none",
                                                    backgroundColor: "rgba(255,255,255,0.18)",
                                                    backdropFilter: "blur(10px) saturate(160%)",
                                                    WebkitBackdropFilter: "blur(10px) saturate(160%)",
                                                    borderColor: theme.palette.primary.main,
                                                    color: theme.palette.primary.main,
                                                    "&:hover": {
                                                        borderColor: theme.palette.primary.main,
                                                        backgroundColor: theme.palette.primary.main,
                                                        color: "#fff",
                                                    },
                                                }}
                                            >
                                                DIRECTIONS
                                            </Button>

                                            <CervinoText
                                                variant="body2"
                                                sx={{ fontWeight: 800, ml: "auto", opacity: 0.95 }}
                                            >
                                                {Number.isFinite(s.distance) ? s.distance.toFixed(1) : "--"} mi
                                            </CervinoText>
                                        </Box>
                                    </Box>
                                </ButtonBase>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Paper>
    );

    const MapPanel = (
        <Box sx={{ flex: 1, width: "100%", height: "100%", position: "relative" }}>
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    opacity: showMapShimmer ? 0 : 1,
                    transition: "opacity 200ms ease",
                }}
            >
                <MapBoxMap
                    key={userCoords ? "geo-on" : "geo-off"}
                    accessToken={process.env.REACT_APP_MAPBOX_TOKEN}
                    userCoords={userCoords}
                    stores={mapStores}
                    fitStores={fitStores}
                    initialCenter={userCoords ?? DEFAULT_CENTER}
                    initialZoom={userCoords ? 10 : 3}
                    fitToStoresOnLoad={!!userCoords}
                    fitToStoresOnUpdate={false}
                    fitBoundsPadding={80}
                    fitBoundsMaxZoom={12}
                    selectedStoreId={selectedStoreId}
                    selectedStoreZoom={13}
                    onMapReady={() => setMapReady(true)}
                />
            </Box>

            <ShimmerOverlay show={showMapShimmer} borderRadius={2} darkness={0.70} />
        </Box>
    );

    const PromoCards = (
        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Stack
                direction={{ xs: "column", sm: "column", md: "row" }}
                justifyContent="center"
                alignItems={{ xs: "center", md: "stretch" }}
                sx={{
                    width: "100%",
                    gap: { xs: 2, sm: 2, md: 3, lg: 5, xl: 7 },
                    px: { xs: 5, sm: 3, md: 4 },
                }}
            >
                <PromoCtaCard
                    cardWidth={{ xs: "80%", sm: "60%", md: "60%", lg: "25%", xl: "20%" }}
                    icon={
                        <LocalShippingIcon
                            sx={{
                                color: theme.palette.primary.main,
                                fontSize: { xs: 60, sm: 60, md: 70, lg: 80, xl: 80 },
                            }}
                        />
                    }
                    title="Amazon"
                    description="Your favorite San Miguel meals delivered fast."
                    ctaLabel="Shop on Amazon"
                />

                <PromoCtaCard
                    cardWidth={{ xs: "80%", sm: "60%", md: "60%", lg: "25%", xl: "20%" }}
                    icon={
                        <StoreIcon
                            sx={{
                                color: theme.palette.primary.main,
                                fontSize: { xs: 60, sm: 60, md: 70, lg: 80, xl: 80 },
                            }}
                        />
                    }
                    title="Retail Stores"
                    description="Find us in select retailers across the U.S."
                    ctaLabel="Find a store"
                    onCtaClick={scrollToMap}
                />

                <PromoCtaCard
                    cardWidth={{ xs: "80%", sm: "60%", md: "60%", lg: "25%", xl: "20%" }}
                    icon={
                        <FoodBankTwoToneIcon
                            sx={{
                                color: theme.palette.primary.main,
                                fontSize: { xs: 60, sm: 60, md: 70, lg: 80, xl: 80 },
                            }}
                        />
                    }
                    title="Food Service"
                    description="For offices, schools, hospitals, venues, and large-volume partners."
                    ctaLabel="Contact Sales"
                    onCtaClick={openContactSalesDialog}
                />
            </Stack>
        </Box>
    );

    return (
        <Stack sx={{ position: "relative", zIndex: 1 }}>
            {/* Header */}
            <Stack alignItems="center" textAlign="center">
                <Box paddingTop={{ xs: 10, sm: 15 }}>
                    <MarketingPhrasesRowV2
                        phrases={["Find our ready to eat Mexican meals near you"]}
                        iconSrc={iconUrl}
                        iconRepeat={2}
                        iconSize={{ xs: 30, sm: 35 }}
                        gap={{ xs: 1, sm: 2 }}
                        color={theme.palette.primary.main}
                        rotateIcon
                    />
                </Box>

                <Box>
                    <CervinoHeaderText
                        color={theme.palette.primary.main}
                        sx={{
                            fontSize: { xs: "1.8rem", sm: "3rem", md: "4rem", lg: "5rem", xl: "6rem" },
                        }}
                    >
                        WHERE TO BUY SAN MIGUEL
                    </CervinoHeaderText>
                </Box>
            </Stack>

            {/* Map + Nearby (responsive order) */}
            {isSmall ? (
                // xs/sm: Map -> Nearby -> Cards
                <Stack spacing={1}>
                    <Box ref={mapAnchorRef} paddingX={2} sx={{ height: "65vh", minHeight: 420, width: "100%", mt: 1 }}>
                        {MapPanel}
                    </Box>

                    <Box paddingX={2}>
                        {NearbyLocationsPanel}
                    </Box>

                    <IconDividerLine
                        iconSrc={iconUrl}
                        iconSize={{ xs: 50, sm: 40, md: 50, lg: 50, xl: 50 }}
                        iconGap={1}
                        rotateIcon
                        pixelsPerSecond={30}
                    />

                    {PromoCards}
                </Stack>
            ) : (
                // md+: Cards ABOVE, then Nearby (left) + Map (right)
                <Stack spacing={0.5}>
                    {PromoCards}

                    <IconDividerLine
                        iconSrc={iconUrl}
                        iconSize={{ xs: 50, sm: 40, md: 50, lg: 50, xl: 50 }}
                        iconGap={1}
                        rotateIcon
                        pixelsPerSecond={30}
                    />

                    <Box ref={mapAnchorRef} sx={{ display: "flex", height: "70vh", minHeight: 560, px: 3 }}>
                        {NearbyLocationsPanel}
                        {MapPanel}
                    </Box>
                </Stack>
            )}
            <Box paddingTop={{xs: 2, sm: 2, md: 3, lg: 3, xl: 3 }}>
                <SMFooter />
            </Box>

            <Dialog
                open={contactSalesOpen}
                onClose={closeContactSalesDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ pr: 6, fontWeight: 900, color: theme.palette.primary.main }}>
                    Contact Sales
                    <IconButton
                        onClick={closeContactSalesDialog}
                        aria-label="close"
                        sx={{ position: "absolute", right: 12, top: 12 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent>
                    <CervinoText sx={{ fontWeight: 700, mb: 2, fontSize: 18 }}>
                        How would you like to reach us?
                    </CervinoText>

                    <Stack spacing={1.5}>
                        <SMButtonBrown
                            startIcon={<WhatsAppIcon />}
                            href={`https://wa.me/${WHATSAPP_NUMBER}`}
                            target="_blank"
                            onClick={closeContactSalesDialog}
                            sx={{fontSize: 24}}
                        >
                            SEND WHATSAPP
                        </SMButtonBrown>

                        <SMButtonBrown
                            startIcon={<EmailIcon />}
                            href={`mailto:${SALES_EMAIL}`}
                            onClick={closeContactSalesDialog}
                            sx={{fontSize: 24}}
                        >
                            EMAIL US
                        </SMButtonBrown>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Stack>
    );
}